import React, { useState, useEffect, useContext } from 'react';
import { FaTimes, FaSearch, FaQuestionCircle, FaInfoCircle, FaRoad, FaUserCog } from 'react-icons/fa';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';
import AuthContext from '../../context/AuthContext';
import './Help.css';

const HelpModal = ({ isOpen, onClose }) => {
  const { user } = useContext(AuthContext);
  const [faqContent, setFaqContent] = useState('');
  const [howItWorksContent, setHowItWorksContent] = useState('');
  const [roadmapContent, setRoadmapContent] = useState('');
  const [adminGuideContent, setAdminGuideContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('faq'); // Default to FAQ tab

  useEffect(() => {
    const fetchContent = async () => {
      try {
        setLoading(true);
        
        // Fetch FAQ content
        const faqResponse = await axios.get('/api/help/faq');
        setFaqContent(faqResponse.data);
        
        // Fetch How It Works content
        const howItWorksResponse = await axios.get('/api/help/how-it-works');
        setHowItWorksContent(howItWorksResponse.data);
        
        // Fetch Development Roadmap content
        try {
          const roadmapResponse = await axios.get('/api/help/roadmap');
          setRoadmapContent(roadmapResponse.data);
        } catch (roadmapErr) {
          console.log('Using fallback roadmap content due to API error');
          // Fallback content if API fails
          setRoadmapContent(`# Business Resilience Navigator - Development Roadmap

This document outlines the planned development roadmap for the Business Resilience Navigator application. Features are organized by release year and marked with their current status.

## Table of Contents
- [Year 1 (Current)](#year-1-current)
- [Year 2 (Planned)](#year-2-planned)
- [Year 3 (Future)](#year-3-future)

## Year 1 (Current)

### Core Features (Completed)
- User authentication and authorization
- Organization management
- Assessment template library
- Assessment creation and completion`);
        }
        
        // Fetch Admin Guide content if user is an admin
        if (user && (user.isAdmin || user.role === 'admin')) {
          console.log('User is admin, setting admin guide content');
          // Use hardcoded content since we're having API issues
          const adminGuideContent = `# Business Resilience Navigator - Administrator Guide

## Table of Contents
- [Introduction](#introduction)
- [Administrator Responsibilities](#administrator-responsibilities)
- [User Management](#user-management)
  - [Adding Users](#adding-users)
  - [Managing User Roles](#managing-user-roles)
  - [Deactivating Users](#deactivating-users)
  - [Multi-Organization Users](#multi-organization-users)
- [Organization Management](#organization-management)
  - [Creating Organizations](#creating-organizations)
  - [Editing Organizations](#editing-organizations)
  - [Deleting Organizations](#deleting-organizations)
  - [Assigning Users to Organizations](#assigning-users-to-organizations)
- [Template Management](#template-management)
  - [Template Library](#template-library)
  - [Creating Templates](#creating-templates)
  - [Editing Templates](#editing-templates)
  - [Assigning Templates to Folders](#assigning-templates-to-folders)
- [System Configuration](#system-configuration)
  - [Global Settings](#global-settings)
  - [Email Configuration](#email-configuration)
  - [API Integrations](#api-integrations)
- [Data Management](#data-management)
  - [Backup and Restore](#backup-and-restore)
  - [Data Export](#data-export)
  - [Data Tables](#data-tables)
  - [Drop Lists](#drop-lists)
- [Troubleshooting](#troubleshooting)
  - [Common Issues](#common-issues)
  - [Error Logs](#error-logs)
  - [Support Resources](#support-resources)

## Introduction

This Administrator Guide provides comprehensive instructions for managing the Business Resilience Navigator platform. As an administrator, you have enhanced privileges and responsibilities for maintaining the system, managing users, and configuring organizational settings.

## Administrator Responsibilities

As an administrator, your key responsibilities include:
- Managing user accounts and access permissions
- Configuring and maintaining organizations
- Managing the template library
- Monitoring system performance and security
- Providing support to end users
- Configuring system settings and integrations

## User Management

### Adding Users

1. Navigate to "User Management" in the sidebar
2. Click the "Add User" button
3. Fill in the required information:
   - Full Name
   - Email Address
   - Password (or generate a random password)
   - Role (User or Admin)
   - Organization Assignment
4. Click "Create User" to add the user to the system
5. The system will notify the user with their login credentials

**Note:** Each organization can have multiple users. The system will display the current user count for each organization.

### Managing User Roles

1. Navigate to "User Management" in the sidebar
2. Find the user whose role you want to change
3. Click the "Edit" button next to their name
4. Change their role using the dropdown menu
5. Click "Save Changes" to apply the new role

Available roles:
- **User**: Standard access to assessments and reports
- **Admin**: Full access to all system features and settings

### Deactivating Users

1. Navigate to "User Management" in the sidebar
2. Find the user you want to deactivate
3. Click the "Deactivate" button
4. Confirm the deactivation when prompted

Deactivated users will no longer be able to log in to the system, but their data will be preserved.

### Multi-Organization Users

For users who need access to multiple organizations:

1. Navigate to "User Management" in the sidebar
2. Find the user you want to modify
3. Click the "Edit" button
4. Check the "Multi-Organization Access" option
5. Assign the user to additional organizations as needed
6. Click "Save Changes" to apply the settings

Multi-organization users can switch between organizations using the organization selector in the header.

## Organization Management

### Creating Organizations

1. Navigate to "Organization Structure" in the sidebar
2. Click "Add Organization"
3. Fill in the organization details:
   - Organization Name
   - Industry
   - Size
   - Address
   - Contact Information
4. Click "Create Organization" to add the new organization

### Editing Organizations

1. Navigate to "Organization Structure" in the sidebar
2. Select the organization you want to edit
3. Click "Edit Organization"
4. Update the organization details as needed
5. Click "Save Changes" to apply your updates

### Deleting Organizations

**Note:** Deleting an organization will permanently remove all associated data, including assessments, reports, and user assignments. This action cannot be undone.

1. Navigate to "Organization Structure" in the sidebar
2. Select the organization you want to delete
3. Click "Delete Organization"
4. Type the organization name to confirm deletion
5. Click "Delete" to permanently remove the organization

### Assigning Users to Organizations

1. Navigate to "Organization Structure" in the sidebar
2. Select the organization you want to manage
3. Click the "User Management" tab
4. To add an existing user:
   - Click "Assign Existing User"
   - Select a user from the dropdown menu
   - Click "Assign" to add them to the organization
5. To create a new user for this organization:
   - Click "Add New User"
   - Fill in the user details
   - Click "Create User" to add them to the organization

## Template Management

### Template Library

The Template Library contains all assessment templates available in the system. As an administrator, you can:
- View all templates
- Create new templates
- Edit existing templates
- Assign templates to folders
- Activate or deactivate templates

### Creating Templates

1. Navigate to "Template Library" in the sidebar
2. Click "Create New Template"
3. Fill in the template details:
   - Template Name
   - Industry Category
   - Description
4. Add categories and questions to your template:
   - Click "Add Category" to create a new section
   - Click "Add Question" within a category to add questions
   - For each question, define:
     - Question text
     - Maturity level descriptions
     - Applicable standards
     - Help text
5. Click "Save Template" when finished

### Editing Templates

1. Navigate to "Template Library" in the sidebar
2. Find the template you want to edit
3. Click the "Edit" button
4. Make your changes to the template structure or content
5. Click "Save Changes" to update the template

### Assigning Templates to Folders

1. Navigate to "Template Library" in the sidebar
2. Find the template you want to assign
3. Click "Assign to Folder"
4. Select the appropriate folder from the dropdown menu
5. Click "Save" to assign the template to the selected folder

## System Configuration

### Global Settings

1. Navigate to "System Settings" in the sidebar
2. Configure global application settings:
   - Application Name
   - Default Language
   - Session Timeout
   - Password Policy
   - File Upload Limits

### Email Configuration

1. Navigate to "System Settings" in the sidebar
2. Select the "Email" tab
3. Configure email settings:
   - SMTP Server
   - Port
   - Authentication Credentials
   - Default Sender Address
   - Email Templates

### API Integrations

1. Navigate to "System Settings" in the sidebar
2. Select the "Integrations" tab
3. Configure API integrations:
   - OpenAI API Key and Settings
   - External Service Connections
   - Webhook Configurations

## Data Management

### Backup and Restore

1. Navigate to "Data Management" in the sidebar
2. Select the "Backup & Restore" tab
3. To create a backup:
   - Click "Create Backup"
   - Select the data to include
   - Click "Start Backup"
4. To restore from a backup:
   - Select a backup from the list
   - Click "Restore"
   - Confirm the restoration

### Data Export

1. Navigate to "Data Management" in the sidebar
2. Select the "Export" tab
3. Choose the data you want to export:
   - Assessment Data
   - User Data
   - Organization Data
   - Template Data
4. Select the export format (CSV, JSON, Excel)
5. Click "Export" to download the data

### Data Tables

1. Navigate to "Data Tables" in the sidebar
2. Select the table you want to manage
3. You can:
   - Add new entries
   - Edit existing entries
   - Delete entries
   - Import data from CSV
   - Export data to CSV

### Drop Lists

1. Navigate to "Drop Lists" in the sidebar
2. Select the drop list you want to manage
3. You can:
   - Add new options
   - Edit existing options
   - Reorder options
   - Delete options

## Troubleshooting

### Common Issues

#### User Cannot Log In

1. Verify the user's account status in User Management
2. Check if the user's password needs to be reset
3. Ensure the user is entering the correct email address
4. Check for any system-wide authentication issues

#### Assessment Not Saving

1. Check the database connection status
2. Verify the user has appropriate permissions
3. Check for any validation errors in the assessment data
4. Look for related errors in the system logs

#### Template Issues

1. Verify the template is properly configured
2. Check for any missing or invalid questions
3. Ensure the template is assigned to the correct folder
4. Verify the template is active and available to users

### Error Logs

1. Navigate to "System Settings" in the sidebar
2. Select the "Logs" tab
3. You can:
   - View system error logs
   - Filter logs by date, severity, or component
   - Export logs for further analysis
   - Clear logs when no longer needed

### Support Resources

If you encounter issues that cannot be resolved using this guide, please contact support:

- Email: support@businessresiliencenavigator.com
- Phone: (555) 123-4567
- Support Portal: https://support.businessresiliencenavigator.com

Please provide the following information when contacting support:
- Your name and organization
- Detailed description of the issue
- Steps to reproduce the problem
- Any error messages or screenshots
- System logs (if available)`;
          
          setAdminGuideContent(adminGuideContent);
        } else {
          console.log('User is not admin or not logged in:', user);
          // Set a message for non-admin users who somehow access this tab
          setAdminGuideContent('# Administrator Guide\n\nThis content is only available to administrators.');
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching help content:', err);
        setError('Failed to load help content. Please try again later.');
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchContent();
    }
  }, [isOpen, user]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Get the active content based on the selected tab
  const getActiveContent = () => {
    console.log('Current active tab:', activeTab);
    console.log('Admin guide content available:', !!adminGuideContent);
    
    let content = '';
    switch (activeTab) {
      case 'faq':
        content = faqContent;
        break;
      case 'how-it-works':
        content = howItWorksContent;
        break;
      case 'roadmap':
        content = roadmapContent;
        break;
      case 'admin-guide':
        content = adminGuideContent || '# Administrator Guide\n\nLoading content...';
        console.log('Admin guide content length:', content.length);
        break;
      default:
        content = faqContent;
    }
    return content;
  };
  
  // Filter content based on search term
  const filteredContent = searchTerm 
    ? getActiveContent().split('\n').filter(line => 
        line.toLowerCase().includes(searchTerm.toLowerCase())
      ).join('\n')
    : getActiveContent();

  if (!isOpen) return null;

  // Check if user is admin
  const isAdmin = user && (user.isAdmin || user.role === 'admin');

  return (
    <div className="help-modal-overlay" style={{ display: isOpen ? 'flex' : 'none', zIndex: 2000 }}>
      <div className="help-modal">
        <div className="help-modal-header">
          <h2>Business Resilience Navigator Help</h2>
          <div className="help-search-container">
            <FaSearch className="help-search-icon" />
            <input
              type="text"
              placeholder="Search help topics..."
              value={searchTerm}
              onChange={handleSearch}
              className="help-search-input"
            />
          </div>
          <button className="help-close-btn" onClick={onClose}>
            <FaTimes />
          </button>
        </div>
        
        <div className="help-tabs">
          <button 
            className={`help-tab ${activeTab === 'faq' ? 'active' : ''}`}
            onClick={() => setActiveTab('faq')}
          >
            <FaQuestionCircle className="help-tab-icon" />
            Frequently Asked Questions
          </button>
          <button 
            className={`help-tab ${activeTab === 'how-it-works' ? 'active' : ''}`}
            onClick={() => setActiveTab('how-it-works')}
          >
            <FaInfoCircle className="help-tab-icon" />
            How This Works
          </button>
          <button 
            className={`help-tab ${activeTab === 'roadmap' ? 'active' : ''}`}
            onClick={() => setActiveTab('roadmap')}
          >
            <FaRoad className="help-tab-icon" />
            Development Roadmap
          </button>
          {isAdmin && (
            <button 
              className={`help-tab ${activeTab === 'admin-guide' ? 'active' : ''}`}
              onClick={() => setActiveTab('admin-guide')}
            >
              <FaUserCog className="help-tab-icon" />
              Administrator Guide
            </button>
          )}
        </div>
        
        <div className="help-modal-content">
          {loading ? (
            <div className="help-loading">Loading help content...</div>
          ) : error ? (
            <div className="help-error">{error}</div>
          ) : (
            <div className="help-faq-content">
              <ReactMarkdown>{filteredContent}</ReactMarkdown>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HelpModal;
